// Money arithmetic — integer cents, VAT, rounding (modules-and-api.md §2.6; A12, rule 7, BR-28).
// No other file may divide or multiply a monetary value.
import type { Bp, Cents } from "@visionx/contracts";
import { assertInt, roundHalfUp } from "./arith";

// Sprint 4: selling prices from costs (markup, the rounding rules, adding VAT) and stock costing (the weighted average,
// quantity × amount, net of discount, backing a unit's cost out) — TC-MONEY-006..022.
export { addVat, markup, roundPrice } from "./pricing";
export { backOutCost, netOfDiscount, timesQuantity, weightedAverageCost } from "./costing";
// Sprint 4: an amount of rand written as text — a price list's cell — as cents, never through a float (TC-MONEY-023).
export { randToCents, type RandTextError, type RandTextResult } from "./parse";

/** VAT-exclusive amount of a VAT-inclusive figure: round(total / (1 + rate)), half-up to the cent. */
export function exVat(inclusive: Cents, rateBp: Bp): Cents {
  assertInt(inclusive, "inclusive"); assertInt(rateBp, "rateBp");
  return roundHalfUp(inclusive * 10_000, 10_000 + rateBp) as Cents;
}

/** VAT portion of a VAT-inclusive figure. Always inclusive − exVat, so line = ex + vat exactly. */
export function vatOf(inclusive: Cents, rateBp: Bp): Cents {
  return (inclusive - exVat(inclusive, rateBp)) as Cents;
}

/** base × percentage, half-up. 15% of R 2 450.00 → applyPercent(245000, 1500) = 36750. */
export function applyPercent(base: Cents, pct: Bp): Cents {
  assertInt(base, "base"); assertInt(pct, "pct");
  return roundHalfUp(base * pct, 10_000) as Cents;
}

/**
 * Split `total` across `weights` so the parts sum exactly to `total`.
 * Largest-remainder method; ties broken by descending position, so the same input always gives the same split.
 * This is the only way a document-level discount or an Overall-Max cap reaches lines (rules 4 and 5).
 */
export function apportion(total: Cents, weights: readonly Cents[]): Cents[] {
  assertInt(total, "total");
  if (weights.length === 0) return [];
  for (const w of weights) { assertInt(w, "weight"); if (w < 0) throw new RangeError("weights must be non-negative"); }
  const sumW = weights.reduce((a, b) => a + b, 0);
  if (sumW === 0) {
    // Nothing to weight by: give everything to the first line, deterministic and total-preserving.
    return weights.map((_, i) => (i === 0 ? total : 0) as Cents);
  }
  const sign = total < 0 ? -1 : 1; const abs = Math.abs(total);
  const raw = weights.map((w) => (abs * w) / sumW);
  const floors = raw.map((r) => Math.floor(r));
  let remainder = abs - floors.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => (b.frac - a.frac) || (b.i - a.i));
  for (const { i } of order) { if (remainder <= 0) break; floors[i] = (floors[i] ?? 0) + 1; remainder--; }
  return floors.map((f) => (sign * f) as Cents);
}

/** Sum of cents with an integer guard. */
export function sumCents(values: readonly Cents[]): Cents {
  let s = 0; for (const v of values) { assertInt(v, "value"); s += v; } return s as Cents;
}
